// =============================================================================
// facility-ownership-service.ts — caller-supplied FacilityOwnership state slice
//
// Tracks which player owns which facility at which tile. Rules-engine treats
// this as opaque state input/output — no module-level registry, no singletons.
//
// All public APIs:
//   • assertFiniteInt on integer params
//   • deepFreeze on returned states
//   • ActionLog appended explicitly
//   • Typed errors (FacilityOwnershipError) with code discriminant
//
// Web port note: Unity never built Cluster F in C# — this is a pure-web design
// consistent with Unity-Snapshot §2.3 locked rules (decom=50% refund).
// =============================================================================

import type { FacilityType } from "@gdg/shared";
import { deepFreeze } from "../_freeze.js";
import type { ActionLog, JsonValue } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export interface OwnedFacility {
  readonly tileIndex: number;
  readonly ownerSeat: number;
  readonly facilityType: FacilityType;
  /** Cost originally paid when this facility was built (used for 50% decom refund). */
  readonly buildCostPaid: number;
}

export interface FacilityOwnershipState {
  readonly facilities: ReadonlyArray<OwnedFacility>;
}

// ---------------------------------------------------------------------------
// Typed error
// ---------------------------------------------------------------------------

export class FacilityOwnershipError extends Error {
  readonly code:
    | "TILE_EMPTY"
    | "TILE_OCCUPIED"
    | "WRONG_OWNER"
    | "INVALID_SEAT"
    | "INVALID_TILE"
    | "INVALID_BUILD_COST";

  constructor(code: FacilityOwnershipError["code"], message: string) {
    super(message);
    this.name = "FacilityOwnershipError";
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createFacilityOwnershipState(): FacilityOwnershipState {
  return deepFreeze({ facilities: [] });
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function getByTile(state: FacilityOwnershipState, tileIndex: number): OwnedFacility | null {
  assertFiniteInt(tileIndex, "tileIndex");
  return state.facilities.find((f) => f.tileIndex === tileIndex) ?? null;
}

export function getByOwner(
  state: FacilityOwnershipState,
  seat: number,
): ReadonlyArray<OwnedFacility> {
  assertFiniteInt(seat, "seat");
  return state.facilities.filter((f) => f.ownerSeat === seat);
}

// ---------------------------------------------------------------------------
// Mutations — all return { newState, log }
// ---------------------------------------------------------------------------

export interface FacilityMutateResult {
  readonly newState: FacilityOwnershipState;
  readonly log: ActionLog;
}

/** Add a facility to the ownership registry. Throws if tile is already occupied. */
export function addFacility(
  state: FacilityOwnershipState,
  log: ActionLog,
  owned: OwnedFacility,
): FacilityMutateResult {
  assertFiniteInt(owned.tileIndex, "owned.tileIndex");
  assertFiniteInt(owned.ownerSeat, "owned.ownerSeat");
  assertFiniteInt(owned.buildCostPaid, "owned.buildCostPaid");
  if (owned.tileIndex < 0) {
    throw new FacilityOwnershipError(
      "INVALID_TILE",
      `tileIndex must be ≥ 0, got ${owned.tileIndex}`,
    );
  }
  if (owned.ownerSeat < 0) {
    throw new FacilityOwnershipError(
      "INVALID_SEAT",
      `ownerSeat must be ≥ 0, got ${owned.ownerSeat}`,
    );
  }
  if (owned.buildCostPaid < 0) {
    throw new FacilityOwnershipError(
      "INVALID_BUILD_COST",
      `buildCostPaid must be ≥ 0, got ${owned.buildCostPaid}`,
    );
  }
  const existing = getByTile(state, owned.tileIndex);
  if (existing !== null) {
    throw new FacilityOwnershipError(
      "TILE_OCCUPIED",
      `Tile ${owned.tileIndex} already has a facility (owner seat ${existing.ownerSeat}).`,
    );
  }
  const nextLog = log.append({
    kind: "facilityOwnership.added",
    data: {
      tileIndex: owned.tileIndex,
      ownerSeat: owned.ownerSeat,
      facilityType: owned.facilityType,
      buildCostPaid: owned.buildCostPaid,
    } as JsonValue,
  });
  const newState = deepFreeze({
    facilities: [...state.facilities, deepFreeze({ ...owned })],
  });
  return { newState, log: nextLog };
}

/** Remove a facility from the ownership registry. Throws if tile is empty. */
export function removeFacility(
  state: FacilityOwnershipState,
  log: ActionLog,
  tileIndex: number,
): FacilityMutateResult {
  assertFiniteInt(tileIndex, "tileIndex");
  if (tileIndex < 0) {
    throw new FacilityOwnershipError("INVALID_TILE", `tileIndex must be ≥ 0, got ${tileIndex}`);
  }
  const existing = getByTile(state, tileIndex);
  if (existing === null) {
    throw new FacilityOwnershipError("TILE_EMPTY", `Tile ${tileIndex} has no facility to remove.`);
  }
  const nextLog = log.append({
    kind: "facilityOwnership.removed",
    data: {
      tileIndex,
      ownerSeat: existing.ownerSeat,
      facilityType: existing.facilityType,
    } as JsonValue,
  });
  const newState = deepFreeze({
    facilities: state.facilities.filter((f) => f.tileIndex !== tileIndex),
  });
  return { newState, log: nextLog };
}

/** Transfer a facility's ownership to a new seat. Throws if tile is empty. */
export function transferFacility(
  state: FacilityOwnershipState,
  log: ActionLog,
  tileIndex: number,
  newOwnerSeat: number,
): FacilityMutateResult {
  assertFiniteInt(tileIndex, "tileIndex");
  assertFiniteInt(newOwnerSeat, "newOwnerSeat");
  if (tileIndex < 0) {
    throw new FacilityOwnershipError("INVALID_TILE", `tileIndex must be ≥ 0, got ${tileIndex}`);
  }
  if (newOwnerSeat < 0) {
    throw new FacilityOwnershipError(
      "INVALID_SEAT",
      `newOwnerSeat must be ≥ 0, got ${newOwnerSeat}`,
    );
  }
  const existing = getByTile(state, tileIndex);
  if (existing === null) {
    throw new FacilityOwnershipError(
      "TILE_EMPTY",
      `Tile ${tileIndex} has no facility to transfer.`,
    );
  }
  const nextLog = log.append({
    kind: "facilityOwnership.transferred",
    data: {
      tileIndex,
      fromSeat: existing.ownerSeat,
      toSeat: newOwnerSeat,
      facilityType: existing.facilityType,
    } as JsonValue,
  });
  const updated: OwnedFacility = deepFreeze({
    tileIndex: existing.tileIndex,
    ownerSeat: newOwnerSeat,
    facilityType: existing.facilityType,
    buildCostPaid: existing.buildCostPaid,
  });
  const newState = deepFreeze({
    facilities: state.facilities.map((f) => (f.tileIndex === tileIndex ? updated : f)),
  });
  return { newState, log: nextLog };
}
